import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe } from 'lucide-react';
import { getPrimaryHost } from '@/config/domains';
import { setUserDomainPreference } from '@/utils/geolocation';
import { buildSiteLanguageRouteMap } from '@/config/destinationRoutes';

const selectedLanguageStyle = {
  backgroundColor: 'hsl(43, 74%, 36%)',
  color: 'white',
} as const;

const LanguageSelector = () => {
  const { language, setLanguage } = useLanguage();

  const handleLanguageChange = (newLanguage: 'pt' | 'en') => {
    setLanguage(newLanguage);
    
    // Save user's domain preference (they manually selected)
    const domain = newLanguage === 'pt' ? 'pt' : 'com';
    setUserDomainPreference(domain);
    
    // Update URL to reflect language change
    const currentPath = window.location.pathname;
    
    const routeMappings = buildSiteLanguageRouteMap();

    const mapping = routeMappings[currentPath];
    const newPath = mapping ? mapping[newLanguage] : currentPath;
    
    if (window.location.hostname === 'localhost') {
      window.location.href = newPath;
      return;
    }
    
    const newHost = getPrimaryHost(newLanguage);
    window.location.href = `https://${newHost}${newPath}`;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px] h-11 w-11 p-0 hover:bg-accent/10">
          <Globe className="h-5 w-5" />
          <span className="sr-only">Select language</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-32 md:w-36">
        <DropdownMenuItem 
          onClick={() => handleLanguageChange('pt')}
          className={`cursor-pointer transition-colors ${language === 'pt' ? 'focus:bg-[hsl(43,74%,36%)] focus:text-white' : 'text-foreground hover:bg-accent/10'}`}
          style={language === 'pt' ? selectedLanguageStyle : undefined}
        >
          <span className="mr-2 text-base">🇵🇹</span>
          <span className="text-sm font-medium">Português</span>
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleLanguageChange('en')}
          className={`cursor-pointer transition-colors ${language === 'en' ? 'focus:bg-[hsl(43,74%,36%)] focus:text-white' : 'text-foreground hover:bg-accent/10'}`}
          style={language === 'en' ? selectedLanguageStyle : undefined}
        >
          <span className="mr-2 text-base">🇬🇧</span>
          <span className="text-sm font-medium">English</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
